import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import type { LucideIcon } from 'lucide-react';
import {
  Baby,
  BookOpen,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  Contact,
  FileText,
  FolderOpen,
  GraduationCap,
  LayoutDashboard,
  LayoutGrid,
  MessageSquare,
  Receipt,
  ShieldCheck,
  Timer,
  UsersRound,
  UserCheck,
  UserPlus,
  Users,
  UtensilsCrossed,
} from 'lucide-react';

import { getModuleDefinition, type WebModule } from '@/lib/modules';

/** The sidebar badge a navigation item can carry (counts come from `useNavBadges`). */
export type NavBadgeKey = 'messages' | 'approvals' | 'absences' | 'timeEntries' | 'overdueInvoices';

export interface NavItemDefinition {
  href: string;
  /** The module whose label and access rule the item uses; `undefined` is the admin dashboard. */
  module?: WebModule;
  /** Label override; by default the module label (one translation per module). */
  label?: MessageDescriptor;
  icon: LucideIcon;
  /** Where the "+" quick action goes; absent means no quick action. */
  addHref?: string;
  badge?: NavBadgeKey;
  /** Routes outside `href` that belong to this item (breadcrumb and active item). */
  alsoUnder?: readonly string[];
}

export interface NavSectionDefinition {
  key: 'home' | 'inbox' | 'children' | 'admissions' | 'team' | 'operations' | 'finance';
  label?: MessageDescriptor;
  items: readonly NavItemDefinition[];
}

/**
 * The sidebar, grouped by the jobs people do: the inbox first, then children
 * and families, admissions, the team, the day, and money. The access-role
 * editor keeps the backend catalog grouping (`MODULE_GROUPS`); only the
 * sidebar groups by job, because that is where a person looks for a page.
 */
export const NAV_SECTIONS: readonly NavSectionDefinition[] = [
  {
    key: 'home',
    items: [
      {
        href: '/',
        label: msg({ id: 'nav.dashboard', message: 'Dashboard' }),
        icon: LayoutDashboard,
        alsoUnder: ['/dashboard'],
      },
    ],
  },
  {
    key: 'inbox',
    label: msg({ id: 'nav.sections.inbox', message: 'Inbox' }),
    items: [
      { href: '/messaging', module: 'messaging', icon: MessageSquare, addHref: '/messaging?action=create', badge: 'messages' },
      { href: '/workflows', module: 'workflows', icon: ClipboardCheck, badge: 'approvals' },
    ],
  },
  {
    key: 'children',
    label: msg({ id: 'nav.sections.childrenFamilies', message: 'Children & families' }),
    items: [
      { href: '/children', module: 'children', icon: Users, addHref: '/children/new' },
      {
        // Admin only (ROUTE_MODULES), so the sidebar shows it to admins alone.
        href: '/children/consents',
        label: msg({ id: 'children.consents.overview.title', message: 'Consent overview' }),
        icon: ShieldCheck,
      },
      { href: '/contacts', module: 'families-contacts', icon: Contact, addHref: '/contacts/create' },
      { href: '/settling-in', module: 'settling-in', icon: Baby, addHref: '/settling-in?action=create' },
      { href: '/portfolio', module: 'portfolio', icon: BookOpen },
    ],
  },
  {
    key: 'admissions',
    label: msg({ id: 'nav.sections.admissions', message: 'Admissions' }),
    items: [{ href: '/prospects', module: 'prospects', icon: UserPlus, addHref: '/prospects/new' }],
  },
  {
    key: 'team',
    label: msg({ id: 'nav.sections.team', message: 'Team' }),
    items: [
      { href: '/employees', module: 'employees', icon: UserCheck, addHref: '/employees/create' },
      { href: '/shifts', module: 'shifts-absences', icon: CalendarClock, badge: 'absences' },
      {
        // Group presence proof (#2114). No module of its own: the item takes its access
        // rule from ROUTE_MODULES (shifts-absences), so each module stays listed once.
        href: '/team/presence',
        label: msg({ id: 'presence.nav.title', message: 'Group presence' }),
        icon: UsersRound,
      },
      { href: '/time-tracking', module: 'time-tracking-workforce', icon: Timer, badge: 'timeEntries' },
    ],
  },
  {
    key: 'operations',
    label: msg({ id: 'nav.sections.dailyOperations', message: 'Daily Operations' }),
    items: [
      { href: '/capacity', module: 'capacity', icon: LayoutGrid },
      { href: '/activities', module: 'attendance-activities', icon: CalendarDays, addHref: '/activities?action=create' },
      { href: '/kitchen', module: 'kitchen', icon: UtensilsCrossed },
      { href: '/menu-activities', module: 'weekly-activities-menu', icon: CalendarCheck },
      { href: '/calendar', module: 'event-calendar', icon: Calendar, addHref: '/calendar?action=create' },
      { href: '/courses', module: 'courses', icon: GraduationCap },
      { href: '/documents', module: 'documents', icon: FolderOpen, addHref: '/documents?action=create' },
    ],
  },
  {
    key: 'finance',
    label: msg({ id: 'nav.sections.finance', message: 'Finance' }),
    items: [
      {
        href: '/contracts',
        module: 'contracts-tariffs',
        icon: FileText,
        addHref: '/contracts?action=new-contract',
        alsoUnder: ['/contract-terminations'],
      },
      { href: '/billing', module: 'billing', icon: Receipt, badge: 'overdueInvoices' },
    ],
  },
];

export const NAV_ITEMS: readonly NavItemDefinition[] = NAV_SECTIONS.flatMap((section) => section.items);

/** The label of a navigation item: the module label unless the item overrides it. */
export function navItemLabel(item: NavItemDefinition): MessageDescriptor {
  if (item.label) return item.label;
  const definition = item.module ? getModuleDefinition(item.module) : undefined;
  return definition?.label ?? msg({ id: 'nav.dashboard', message: 'Dashboard' });
}

/** True when `pathname` is `href` or lies under it (`/children/123/edit` is under `/children`). */
export function isUnderPath(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
}

/** The length of the item path `pathname` lies under (`href` or an `alsoUnder` route), 0 for none. */
function matchLength(pathname: string, item: NavItemDefinition): number {
  return [item.href, ...(item.alsoUnder ?? [])].reduce(
    (best, path) => (isUnderPath(pathname, path) && path.length > best ? path.length : best),
    0,
  );
}

/** True when `pathname` belongs to `item`: under its `href` or one of its `alsoUnder` routes. */
export function isNavItemActive(pathname: string, item: NavItemDefinition): boolean {
  return matchLength(pathname, item) > 0;
}

/**
 * The navigation item a path belongs to, by the longest matching path, with
 * its section. `/billing/invoices` belongs to Billing, `/dashboard/analytics`
 * to the Dashboard; `/settings/*` to none.
 */
export function matchNavItem(
  pathname: string,
): { section: NavSectionDefinition; item: NavItemDefinition } | undefined {
  let best: { section: NavSectionDefinition; item: NavItemDefinition; length: number } | undefined;
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      const length = matchLength(pathname, item);
      if (length > 0 && (!best || length > best.length)) {
        best = { section, item, length };
      }
    }
  }
  return best && { section: best.section, item: best.item };
}
